import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, TrendingUp, TrendingDown, Minus, Info } from "lucide-react";

const InfoTooltip = ({ text }) => (
  <div className="relative inline-block group align-middle ml-1.5 cursor-help">
    <Info size={13} className="text-gray-400 hover:text-[#006080]" />
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

const STATUS_COLORS = {
  success: "#059669", failed: "#DC2626", pending: "#9CA3AF",
  running: "#006080", dry_run: "#7C3AED", skipped: "#9CA3AF", missing: "#E5E7EB",
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#9CA3AF";
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
      style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}>
      {status || "—"}
    </span>
  );
}

export default function ExecutionDiffPage() {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState([]);
  const [exec1Id, setExec1Id] = useState("");
  const [exec2Id, setExec2Id] = useState("");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    client.get("/executions/").then(r => setExecutions(r.data)).catch(() => {});
  }, []);

  const runDiff = async () => {
    if (!exec1Id || !exec2Id) { setError("Select both executions"); return; }
    if (exec1Id === exec2Id) { setError("Select two different executions"); return; }
    setError(""); setLoading(true); setDiff(null);
    try {
      const res = await client.get("/executions/diff", { params: { exec1_id: exec1Id, exec2_id: exec2Id } });
      setDiff(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load diff");
    } finally { setLoading(false); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : "—";
  const execLabel = (e) => `${e.status?.toUpperCase()} · ${e.processed_count ?? 0}/${e.total_rows ?? 0} rows · ${fmtDate(e.created_at)}`;

  const { summary } = diff || {};

  return (
    <div className="p-6 max-w-6xl mx-auto fade-in">
      <div className="mb-6 flex items-start gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Execution Diff</h1>
            <InfoTooltip text="Compare two execution runs side-by-side to see which rows improved, degraded, or stayed the same. Useful for verifying a fix or re-run. Select two executions and click 'Compare'." />
          </div>
          <p className="text-gray-500 text-sm mt-0.5">Compare two runs to see what changed between them</p>
        </div>
      </div>

      {/* Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Execution A (baseline)</p>
            <Select value={exec1Id} onValueChange={setExec1Id}>
              <SelectTrigger data-testid="diff-exec1-select"><SelectValue placeholder="Select execution A..." /></SelectTrigger>
              <SelectContent>
                {executions.map(e => (
                  <SelectItem key={e._id} value={e._id}>
                    #{e._id.slice(-6)} — {execLabel(e)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">Execution B (compare)</p>
            <Select value={exec2Id} onValueChange={setExec2Id}>
              <SelectTrigger data-testid="diff-exec2-select"><SelectValue placeholder="Select execution B..." /></SelectTrigger>
              <SelectContent>
                {executions.map(e => (
                  <SelectItem key={e._id} value={e._id}>
                    #{e._id.slice(-6)} — {execLabel(e)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {error && <div className="mb-3 p-2 bg-red-50 text-red-600 text-sm rounded">{error}</div>}
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={runDiff} disabled={loading} data-testid="diff-compare-btn">
          <ArrowLeftRight size={14} className="mr-2" />
          {loading ? "Comparing..." : "Compare"}
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: "Improved", value: summary.improved, icon: TrendingUp, color: "#059669", bg: "#F0FDF4", desc: "A failed → B success" },
            { label: "Degraded", value: summary.degraded, icon: TrendingDown, color: "#DC2626", bg: "#FEF2F2", desc: "A success → B failed" },
            { label: "Unchanged OK", value: summary.unchanged_success, icon: Minus, color: "#006080", bg: "#F0F9FF", desc: "Success in both" },
            { label: "Unchanged Fail", value: summary.unchanged_failed, icon: Minus, color: "#9CA3AF", bg: "#F9FAFB", desc: "Failed in both" },
          ].map(({ label, value, icon: Icon, color, bg, desc }) => (
            <div key={label} className="rounded-lg p-4 border" style={{ background: bg }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} style={{ color }} />
                <span className="text-xs font-semibold text-gray-600">{label}</span>
              </div>
              <div className="text-3xl font-black" style={{ color }}>{value}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{desc}</div>
            </div>
          ))}
        </div>
      )}

      {/* Row diff table */}
      {diff && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">
              Row-by-Row Comparison ({diff.rows.length} rows)
            </p>
            <div className="flex gap-3 text-xs text-gray-400">
              <span className="font-mono">A: #{exec1Id.slice(-6)}</span>
              <span>vs</span>
              <span className="font-mono">B: #{exec2Id.slice(-6)}</span>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="diff-table">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="text-gray-500 uppercase text-[10px] tracking-wider">
                  <th className="px-3 py-2 text-left w-16">Row</th>
                  <th className="px-3 py-2 text-left">A Status</th>
                  <th className="px-3 py-2 text-left">A HTTP</th>
                  <th className="px-3 py-2 text-left">A Time</th>
                  <th className="px-1 py-2 text-center w-6"></th>
                  <th className="px-3 py-2 text-left">B Status</th>
                  <th className="px-3 py-2 text-left">B HTTP</th>
                  <th className="px-3 py-2 text-left">B Time</th>
                  <th className="px-3 py-2 text-left">Change</th>
                </tr>
              </thead>
              <tbody>
                {diff.rows.map((row) => {
                  const s1 = row.exec1?.status || "missing";
                  const s2 = row.exec2?.status || "missing";
                  const ok1 = s1 === "success";
                  const ok2 = s2 === "success";
                  let changeLabel = null;
                  if (!ok1 && ok2) changeLabel = { text: "IMPROVED", color: "#059669" };
                  else if (ok1 && !ok2) changeLabel = { text: "DEGRADED", color: "#DC2626" };

                  return (
                    <tr key={row.row_index}
                      className={`border-t border-gray-50 hover:bg-gray-50 ${row.changed ? "bg-yellow-50/50" : ""}`}
                      data-testid={`diff-row-${row.row_index}`}
                    >
                      <td className="px-3 py-2 font-mono text-gray-500">{row.row_index + 1}</td>
                      <td className="px-3 py-2"><StatusBadge status={s1} /></td>
                      <td className="px-3 py-2 font-mono text-gray-500">{row.exec1?.http_status || "—"}</td>
                      <td className="px-3 py-2 font-mono text-gray-400">{row.exec1?.response_time_ms ? `${row.exec1.response_time_ms}ms` : "—"}</td>
                      <td className="px-1 py-2 text-gray-300 text-center">→</td>
                      <td className="px-3 py-2"><StatusBadge status={s2} /></td>
                      <td className="px-3 py-2 font-mono text-gray-500">{row.exec2?.http_status || "—"}</td>
                      <td className="px-3 py-2 font-mono text-gray-400">{row.exec2?.response_time_ms ? `${row.exec2.response_time_ms}ms` : "—"}</td>
                      <td className="px-3 py-2">
                        {changeLabel ? (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                            style={{ background: `${changeLabel.color}15`, color: changeLabel.color }}>
                            {changeLabel.text}
                          </span>
                        ) : row.changed ? (
                          <span className="text-[10px] text-gray-400">changed</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!diff && !loading && (
        <div className="py-20 text-center text-gray-400">
          <ArrowLeftRight size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm">Select two executions above and click Compare</p>
        </div>
      )}
    </div>
  );
}
